import React, { memo } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Autocomplete from '@mui/material/Autocomplete';

import useLocales from 'src/hooks/useLocales';

import { ChildElement } from '../types/elementSettingsTypes';

type ElementChildElementsManagerProps = {
  childElements: ChildElement[];
  sameTypeElementsList: ChildElement[];
  selectedChildElement: ChildElement | null;
  onChildInputChange: (newInputValue: ChildElement | null) => void;
  onAddChildElement: () => void;
  onDeleteChildElement: (index: number) => void;
};

const ElementChildElementsManager: React.FC<ElementChildElementsManagerProps> = ({
  childElements,
  sameTypeElementsList,
  selectedChildElement,
  onChildInputChange,
  onAddChildElement,
  onDeleteChildElement,
}) => {
  const { t } = useLocales();

  return (
    <Box>
      <Divider sx={{ mb: 2 }}>
        <Typography variant="body2">{t('forms.elements.settings.addChildEl')}</Typography>
      </Divider>
      <FormGroup
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Autocomplete
            value={selectedChildElement}
            onChange={(event, newInputValue) => {
              onChildInputChange(newInputValue);
            }}
            options={sameTypeElementsList.filter((t) => !childElements.some((c) => c.id === t.id))}
            getOptionLabel={(option) => `${option.name} (${option.id})`}
            fullWidth
            size="small"
            renderInput={(params) => (
              <TextField {...params} label={`${t('forms.elements.settings.sameTypeEl')}`} />
            )}
            id="F335YI"
          />

          <IconButton
            disabled={selectedChildElement === null}
            onClick={() => onAddChildElement()}
            id="R5Txzg"
          >
            <AddIcon color={selectedChildElement ? 'primary' : 'disabled'} />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: 1,
            width: '100%',
            maxHeight: '160px',
            overflow: 'auto',
          }}
        >
          {childElements.length > 0 &&
            childElements.map((t, i) => (
              <Box
                key={`form-template-${t.id}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography noWrap>{`${t.name} (${t.id})`}</Typography>

                <IconButton onClick={() => onDeleteChildElement(i)} id="4FdwWb">
                  <ClearIcon color="error" />
                </IconButton>
              </Box>
            ))}
        </Box>
      </FormGroup>
    </Box>
  );
};

export default memo(ElementChildElementsManager);
